<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import { EMIT } from '@/enum';
import { useOrganizationTypeStore, useTenantStore } from '@/store';
import { storeToRefs } from 'pinia';

const emit = defineEmits([EMIT.NEXT, EMIT.BACK]);
const orgTypeStore = useOrganizationTypeStore();
const tenantStore = useTenantStore();

/**
 * same as tenant checkbox to handle assinging the tenant name to the org
 */
const checked = ref(false);

/**
 * The data ref object used to create the request payload.
 */
const { newOrganization } = storeToRefs(tenantStore);

/**
 * The required validation rules.
 */
const rules = {
  name: { required },
  acronym: {},
  org_type: { required },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, newOrganization);

onMounted(() => {
  init();
});

const init = async () => await orgTypeStore.getOrganizationTypes();

/**
 * Handle the client side validation, followed by saving the
 * information for the new user account.
 */
const onSave = () => {
  if (v$.value.$invalid) return;
  emit(EMIT.NEXT);
};

/**
 * if checbox selected then assign the org name as the tenant name, reverse if unselected
 */
const handleSameAsTenantCheckbox = () => {
  newOrganization.value.name = checked.value ? tenantStore.newTenant.name : '';
};

const goBack = () => emit(EMIT.BACK);
</script>

<template>
  <div class="flex flex-row mt-3">
    <div class="field flex-1 mr-4">
      <div class="flex flex-row justify-content-between">
        <label class="required" for="name">Organization Name</label>
        <div class="field-checkbox mb-2">
          <Checkbox v-model="checked" :binary="true" @input="handleSameAsTenantCheckbox" />
          <label>Same as Tenant</label>
        </div>
      </div>
      <InputText id="name" v-model="v$.name.$model" type="text" placeholder="Organization Name" class="w-full" />
      <div v-for="error of v$.name.$errors" :key="error.$uid" class="input-errors">
        <small>{{ error.$message }}</small>
      </div>
    </div>
    <div class="field w-6rem">
      <label for="address">Acronym</label>
      <InputText
        id="acronym"
        v-model="v$.acronym.$model"
        class="w-full"
        maxlength="5"
        type="text"
        placeholder="e.g ABC"
      />
    </div>
  </div>
  <div class="field">
    <label class="required" for="org_type_id">Organization Type</label>
    <Dropdown
      id="org_type_id"
      v-model="v$.org_type.$model"
      :options="orgTypeStore.types"
      option-label="title"
      option-value="title"
      placeholder="Select a Type"
      class="w-full"
    />
  </div>

  <div class="dialog-footer flex justify-content-between">
    <AppButton icon-left="arrow_back" type="secondary" text="Back" class="ml-0 admin-button" @click="goBack" />
    <AppButton
      icon-left="arrow_forward"
      type="secondary"
      text="Next"
      class="admin-button cursor-pointer"
      :disabled="v$.$invalid"
      @click="onSave"
    />
  </div>
</template>
